import React, { useMemo, useRef, useState } from "react";
import { interpolate, placeholdersFor, greetingForHour } from "../../utils/emailPlaceholders";
import { buildContext } from "../../utils/acceptanceEmailComposer";
import { sendTransactionEmail } from "../../utils/emailSender";
import {
  plainTextToHtml,
  htmlToPlainText,
  wrapBrandedEmail,
  videoCardHtml,
} from "../../utils/emailBrandTemplate";
import { recordSentEmail } from "../../utils/sentEmails";
import RichTextEditor from "./RichTextEditor";
import AttachmentPicker from "./AttachmentPicker";
import VideoPicker from "./VideoPicker";
import { useActionDialog } from "../ActionDialog";

const CHIP =
  "inline-flex items-center gap-1 bg-slate-50 text-linkColor border border-slate-300 rounded px-2 py-0.5 text-[11px] font-medium hover:bg-slate-100";

export default function EmailComposer({
  deal,
  contacts,
  sender,
  email, // { id, subject, body(plain)|bodyHtml, recipientLabel, recipientRole } or the one-off stub
  defaultRecipient, // { name, email }
  initialAttachments = [], // optional pre-seeded attachments (e.g. a Drive PDF)
  onSent,
  onCancel,
  onSaveAsTemplate, // ({ label, subject, body, video }) => Promise — save current draft as a reusable template
}) {
  const ctx = useMemo(
    () => ({
      ...buildContext(deal, contacts || [], {}),
      // {{greeting}} resolves at compose time: Good Morning / Afternoon / Evening.
      greeting: greetingForHour(new Date().getHours()),
    }),
    [deal, contacts],
  );
  const isOneOff = email.id === "one-off";
  const { prompt, dialog } = useActionDialog();

  const [to, setTo] = useState(defaultRecipient?.email || "");
  const [toName, setToName] = useState(defaultRecipient?.name || "");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState(interpolate(email.subject || "", ctx));
  const [bodyHtml, setBodyHtml] = useState(() =>
    interpolate(email.bodyHtml || plainTextToHtml(email.body || ""), ctx));
  const [attachments, setAttachments] = useState(() => initialAttachments || []);
  // Templates can carry a default video (Settings → Email Templates);
  // it arrives pre-attached and the agent can swap or remove it per send.
  const [video, setVideo] = useState(email.video || null);
  const [showPreview, setShowPreview] = useState(true);
  const [sending, setSending] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [error, setError] = useState("");
  const editorRef = useRef(null);

  // Turn whatever the agent just wrote into a reusable template. Saves the RAW
  // text (with any {{tokens}}), not the interpolated preview.
  const handleSaveAsTemplate = async () => {
    const name = await prompt({
      title: "Save as template",
      label: "Name this template",
      placeholder: "Price Improvement",
      confirmLabel: "Save template",
    });
    if (!name || !name.trim()) return;
    setSavingTemplate(true);
    try {
      await onSaveAsTemplate({
        label: name.trim(),
        subject,
        body: bodyHtml,
        video: video || null,
      });
    } finally {
      setSavingTemplate(false);
    }
  };

  // Chips can insert {{tokens}} after the initial fill-in — re-interpolate at
  // render/send time so a chip never goes out as literal text.
  const previewHtml = useMemo(
    () =>
      wrapBrandedEmail({
        contentHtml: interpolate(bodyHtml, ctx),
        sender,
        propertyAddress: deal?.propertyaddress || "",
        videoCard: video
          ? videoCardHtml({
              url: video.url,
              title: video.title,
              thumbnailUrl: video.thumbnailUrl || "",
            })
          : "",
      }),
    [bodyHtml, ctx, sender, video],
  );

  const chips = placeholdersFor(isOneOff ? "" : email.id).flatMap((g) => g.items);

  const handleSend = async () => {
    if (!to) {
      setError("Please enter a recipient email.");
      return;
    }
    setSending(true);
    setError("");
    const res = await sendTransactionEmail({
      userId: sender?.id,
      to,
      toName,
      cc: cc || undefined,
      bcc: bcc || undefined,
      subject: interpolate(subject, ctx),
      text: htmlToPlainText(interpolate(bodyHtml, ctx)),
      html: previewHtml,
      attachments,
      emailId: email.id,
      relatedDealId: deal?.id || deal?.createdat || "",
    });
    if (!res?.ok) {
      setSending(false);
      const hint =
        attachments.length && !/attachment/i.test(res?.error || "")
          ? " (if this keeps failing with attachments, the email backend may need its update)"
          : "";
      setError(`Send failed: ${res?.error || "unknown error"}${hint}`);
      return;
    }
    let historyId = null;
    try {
      historyId = await recordSentEmail({
        dealId: String(deal?.id || deal?.createdat || ""),
        dealAddress: deal?.propertyaddress || "",
        emailId: email.id,
        sequenceSide: deal?.dealtype || "",
        agentId: sender?.id || null,
        agentName: sender?.displayname || "",
        recipientEmail: to,
        recipientName: toName || "",
        cc: cc || "",
        bcc: bcc || "",
        subject: interpolate(subject, ctx),
        bodyHtml: previewHtml,
        attachments: attachments.map(({ name, size, source }) => ({ name, size, source })),
        videoUrl: video?.url || "",
        gmailMessageId: res.gmailMessageId || null,
      });
    } catch (e) {
      console.error("History write failed (email DID send):", e);
    }
    setSending(false);
    onSent?.({
      sentAt: Date.now(),
      sentBy: sender?.id || null,
      recipientEmail: to,
      recipientName: toName || "",
      subject: interpolate(subject, ctx),
      gmailMessageId: res.gmailMessageId || null,
      historyId,
    });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
      <div className="flex items-center justify-between px-5 py-3 border-b">
        <h3 className="font-semibold text-slate-900">
          {isOneOff ? "One-off email" : `${email.recipientLabel}: ${interpolate(email.subject || "", ctx)}`}
        </h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-700">✕</button>
      </div>

      <div className="grid lg:grid-cols-2 gap-0">
        {/* Compose column */}
        <div className="px-5 py-4 space-y-3 border-r border-slate-100">
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">To (email)</label>
              <input
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="recipient@example.com"
                className="w-full text-sm rounded border border-slate-300 px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Name</label>
              <input
                value={toName}
                onChange={(e) => setToName(e.target.value)}
                className="w-full text-sm rounded border border-slate-300 px-2 py-1.5"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">CC (optional)</label>
              <input
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="someone@else.com"
                className="w-full text-sm rounded border border-slate-300 px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">BCC (optional)</label>
              <input
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
                placeholder="quiet copy…"
                className="w-full text-sm rounded border border-slate-300 px-2 py-1.5"
              />
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full text-sm rounded border border-slate-300 px-2 py-1.5"
            />
          </div>
          <div>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {chips.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className={CHIP}
                  title={c.display}
                  onClick={() => editorRef.current?.insertText(`{{${c.key}}}`)}
                >
                  <span>{c.icon}</span>
                  <span>{c.display}</span>
                </button>
              ))}
            </div>
            <RichTextEditor ref={editorRef} value={bodyHtml} onChange={setBodyHtml} />
            <p className="text-[11px] text-slate-400 mt-1">
              [Bracketed] items are blanks to fill in. Edits affect this send only.
            </p>
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Attachments</label>
            <AttachmentPicker deal={deal} attachments={attachments} onChange={setAttachments} />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Video</label>
            <VideoPicker video={video} onChange={setVideo} />
          </div>
        </div>

        {/* Preview column — sticky so it follows as you scroll the compose side */}
        <div className="px-5 py-4 bg-slate-50 rounded-r-2xl">
          <div className="lg:sticky lg:top-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-bold uppercase text-slate-500">What the recipient sees</p>
            <button
              onClick={() => setShowPreview((v) => !v)}
              className="text-xs text-sky-600 font-semibold lg:hidden"
            >
              {showPreview ? "Hide" : "Show"} preview
            </button>
          </div>
          {showPreview && (
            <iframe
              title="Email preview"
              srcDoc={previewHtml}
              className="w-full h-[480px] bg-white rounded-lg border border-slate-200"
            />
          )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 px-5 py-3 border-t">
        {onSaveAsTemplate && (
          <button
            type="button"
            onClick={handleSaveAsTemplate}
            disabled={savingTemplate}
            className="text-sm font-semibold text-slate-700 border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-100 disabled:opacity-50"
          >
            {savingTemplate ? "Saving…" : "＋ Save as Template"}
          </button>
        )}
        <span className="flex-1" />
        <button onClick={onCancel} className="text-sm font-semibold text-slate-600 px-3 py-1.5">
          Cancel
        </button>
        <button
          onClick={handleSend}
          disabled={sending}
          className="text-sm font-semibold text-white bg-themeColor rounded px-4 py-1.5 disabled:opacity-50"
        >
          {sending ? "Sending…" : "Send email"}
        </button>
      </div>
      {dialog}
    </div>
  );
}
